// Types personalisés
import BaseTreeNode from "@/types/BaseTreeNode";
import ObjectWidget from "@/types/Dashboard/ObjectDashboard/ObjectWidget";
import Dictionary from "@/types/Dictionary";
import SaveModeEnum from "@/enums/SaveModeEnum";
import ObjectDashboard from "@/types/Dashboard/ObjectDashboard/ObjectDashboard";
import Pin from "@/types/FilterTree/Pin";
import NodeTypeEnum from "@/enums/NodeTypeEnum";
// Axios pour WebAPI Client
import axios, { AxiosResponse } from "axios";
import { API_URL } from "@/App.settings";
// Store managé
import ObjectDashboardStore from "../ObjectDashboardStore";
// Lib vue.js
import { Vue } from 'vue-property-decorator';

export const EMPTY_OBJECT_DASHBOARD_ID: number = -1

/**
 * Interface de définition des transitions et opérations propre à la machine à état
 */
export interface IObjectDashAdminState{

    /** Demande le nettoyage de la sélection. */
    Clear(): void;

    /** 
     * Demande le chargement de l'arborescence des objets types et tableau de bord "AdminTree"
     * @param nodeIdToSelect identifiant du noeud à pré-sélectionner
     */
    LoadTree(nodeIdToSelect: string): Promise<void>;

    /**
     * Selection un noeud de l'arborescence des objets types
     * @param dashnode noeud à selectionner
     */
    SelectDashNode(dashnode: BaseTreeNode): Promise<void>;

    /**
     * Création d'un tableau de bord dans l'arborescence des objets types
     * @param dashnode noeud lié au nouveau tableau
     * @param profileIds liste des profiles attachés au tableau de bord
     * @param pins liste des pins attachés au tableau de bord
     * @param widgets liste des widgets attaché au tableau de bord
     */
    CreateDashboard(dashnode: BaseTreeNode, profileIds: number[], pins: Pin[], widgets: ObjectWidget[]): Promise<void>;

    /**
     * Supprime un tableau de bord (qu'importe l'état nouveau ou déjà sauvegardé)
     * @param dashnode noeud attaché au tableau que l'on désire supprimer
     */
    RemoveDashboard(dashnode: BaseTreeNode): Promise<void>;

    /**
     * Sauvegarde le tableau de bord en base
     * @param dashboard a sauvegarder
     */
    SaveDashboard(dashboard: ObjectDashboard): Promise<void>;

    /** 
     * Ajoute un widget
     * @param widget ajoute un widget
     */
    AddWidget(widget: ObjectWidget): void;

    /**
     * Retire un widget
     * @param widgetId identifiant du widget que l'on souhaite retirer
     */
    RemoveWidget(widgetId: number): void;

    /**
     * Met à jour les paramètre d'un widget
     * @param widgetId identifiant du widget dont on veux mettre à jour ses paramètres
     * @param parameters nouveaux parametres
     * @param name nom
     */
    UpdateWidgetParameters(widgetId: number, parameters: any, name: string | undefined): void;

    /**
     * Met à jour les données du widget
     * @param widgetId identifiant du widget dont on veux mettre à jour ses paramètres
     * @param datas données du widget
     */
    UpdateWidget(widgetId: number, datas: any): void;
}

/**
 * Classe abstraite qui contient les opération utilitaires commune aux différents états.
 * Défini aussi la structure de base d'un état "ObjectDashAdminState"
 */
export abstract class ObjectDashAdminState implements IObjectDashAdminState {

    /** Context de la machine à état -> store */
    protected store: ObjectDashboardStore;

    /** Constructeur par défaut */
    public constructor(context: ObjectDashboardStore){
        this.store = context;
    }
    
//#region Abstractions
    public abstract Clear(): void;
    public abstract LoadTree(nodeIdToSelect: string): Promise<void>;
    public abstract SelectDashNode(dashnode: BaseTreeNode): Promise<void> ;
    public abstract CreateDashboard(dashnode: BaseTreeNode, profileIds: number[], pins: Pin[], widgets: ObjectWidget[]): Promise<void> ;
    public abstract RemoveDashboard(dashnode: BaseTreeNode): Promise<void>;
    public abstract SaveDashboard(dashboard: ObjectDashboard): Promise<void> ;
    public abstract AddWidget(widget: ObjectWidget): void ;
    public abstract RemoveWidget(widgetId: number): void ;
    public abstract UpdateWidgetParameters(widgetId: number, parameters: any, name: string | undefined): void ;
    public abstract UpdateWidget(widgetId: number, datas: any) ;
//#endregion

    /** 
     * Détermine les paramètres d'un tableau de bord fait qu'il est par défaut ou non
     * @param profileIds liste des profiles du tableau de bord
     * @param pins liste des pins du tableau de bord
     */
    protected isDefault(profileIds: number[] | undefined, pins: Pin[] | undefined): boolean{
        return (!profileIds || profileIds.length == 0) && (!pins || pins.length == 0)
    }

    /**
     * Recherche un noeud dans l'arboescence des objets types
     * @param idToSearch identifiant à rechercher
     * @param nodes arborescence des types d'objets (AdminTree)
     * @returns 
     */
    protected searchNode(idToSearch: string, nodes: BaseTreeNode[]): BaseTreeNode | null {
        for(let node of nodes) {
            if (node.Id == idToSearch) 
                return node;
            else if(node.Children && node.Children.length > 0) {
                let childNode: BaseTreeNode | null = this.searchNode(idToSearch, node.Children);
                if (childNode)
                    return childNode;
            }
        }
        return null;
    }

    /** 
     * Retire un noeud de l'arborescence des type d'objet et tableau de bord
     * @param idToSearch Identifiant du noeud recherché
     * @param nodes Arborescence des types d'objets
     */
    private removeNode(idToSearch: string, nodes: BaseTreeNode[]): void {
        const index = nodes.findIndex(n => n.Id == idToSearch);
        if(index == -1){
            for(let node of nodes) {
                this.removeNode(idToSearch, node.Children);
            }
        }
        else
            nodes.splice(index,1);
    }

    /**
     * Charge les données d'un tableau de bord
     * @param dashboardId 
     * @returns 
     */
    protected async loadDashboard(dashboardId: number): Promise<ObjectDashboard>{
        this.store.context.commit('setIsModified', false);
		const args: Dictionary<any> = {};
		args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
		args["DashboardId"] = dashboardId;

		/** Interroge l'API correspondant au dashboard du dashNode sélectionné */ 
		const response: AxiosResponse<any,any> = await axios.post(API_URL + "ObjectDashboard/Load", args);
		if (response && response.data) {
            const dashboard: ObjectDashboard = response.data;
            /** Inscrit le tableau de bord */
			this.store.context.commit('setObjectDash', dashboard);

			/** S'il ne s'agit pas d'un Id de noeud temporaire (pas un nouvel objet) */ 
			if (Number(dashboardId) > 0)
				this.store.context.commit('setSaveMode', { saveMode: SaveModeEnum.UpdateObject });
            else
                this.store.context.commit('setSaveMode', { saveMode: SaveModeEnum.NewObject })

            return dashboard;
		}
        else
            return undefined;
    }

    /**
     * Nouveau noeud dans l'arborescence des objets types
     * @param name nom du noeud
     * @param sourceDashNode noeud parent du future noeud
     * @param dashboardId identifiant du tableau de bord qui lui sera attaché
     * @returns le nœud créé de manière asynchrone
     */
    protected async newDashNode(name: string, sourceDashNode: BaseTreeNode, dashboardId: number): Promise<BaseTreeNode> {
        // Construit la nouvelle entrée dans l'arbre
		const dashNode: BaseTreeNode = {
			Id: "",
			Name: name,
			Icon: sourceDashNode.Icon,
			Expanded:false,
			Forced: false,
			ObjectType: sourceDashNode.ObjectType,
			Nodetype: NodeTypeEnum.Dashboard,
			DashboardRef: dashboardId,
			Children:[]
		};
		sourceDashNode.Children.push(dashNode);
        return dashNode;
    }

    /**
     * Construit un nouveau tableau de bord
     * @param name nom du future tableau de bord
     * @param newDashbaordId identifiant du future tableau de bord 
     * @param sourceDashNode noeud propriétaire de ce tableau de bord
     * @param profileIds indentifiants des profiles du future tableau de bord
     * @param pins pins du future tableau de bord
     * @param widgets widgets du future tableau de bord
     * @returns le tableau de bord, nouvellement, crée de manière asynchrone.
     */
    protected async newDashboard(name: string, newDashbaordId: number, sourceDashNode: BaseTreeNode, profileIds: number[], pins: Pin[], widgets: ObjectWidget[]){
        return {
			Id: newDashbaordId,
			ObjectType: sourceDashNode.ObjectType,
			Name: name,
			Widgets: widgets,
			ProfileIds: profileIds,
			Pins: pins
		};
    }

    /** 
     * Supprime un taleau de bord
     * @param dashNode Noeud d'arborescence propriétaire du tableau à supprimer 
     */
    protected async removeDashboard(dashNode: BaseTreeNode){
        /** Et-ce que le noeud dispose d'un tableau de bord ? */
        if( dashNode.DashboardRef == null)
            return;

        /** Récupère l'identifiant du tableau de bord */
        const dashboardId = dashNode.DashboardRef ;
        /** Récupère le mode de sauvegarde */
        const saveMode: SaveModeEnum = this.store.context.getters['GetSaveMode']; 

        if(dashboardId > EMPTY_OBJECT_DASHBOARD_ID){
            /** Prépare la supression */
            const args: Dictionary<any> = {};
            args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
            args["ObjectDashboardId"] = dashboardId;

            try{
                await axios.post(API_URL + "ObjectDashboard/Delete", args)
            } catch(err) {
                console.error(err);
                throw err;
            }
        }
        /** On nettoye l'arborescence d'object types*/
        const adminTree: BaseTreeNode[] = this.store.context.getters['GetAdminTree'];
        this.removeNode(dashNode.Id, adminTree);
    }

}